import {
  callEvents,
  CallReason,
  chatEvents,
  CloserSDK,
  externalEvents,
  GroupRoom,
  roomEvents,
  Session
} from "@closerplatform/sdk/src";
import { Artichoke } from "@closerplatform/sdk/src/artichoke/artichoke";
import { Call } from "@closerplatform/sdk/src/calls/call";
import type { HistoryFilter, ID } from "@closerplatform/sdk/src/protocol/protocol";
import { config } from "../config/config";
import { throttle, type DebouncedFunc } from "lodash";
import { EMPTY, firstValueFrom, merge, Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MessageTag } from "../models/messageTag";
import { logger } from "../utils/logger";
import { Timed } from "../utils/timed";

type RoomHandler = (room: GroupRoom) => void;

export interface ArtichokeService {
  disconnectedEvent: Observable<any>;

  connect(id: ID): void;
  disconnect(): void;

  getDeviceId(): Promise<ID> | undefined;

  onConnected(callback: () => void): void;

  send(roomId: ID, text: string, context?: any): Promise<chatEvents.Received>;

  sendCustom(roomId: ID, text: string, tag: string, context: any): Promise<chatEvents.Received>;

  indicateTyping(roomId: ID, preview?: string): Promise<void>;
  indicateTypingThrottled: DebouncedFunc<(roomId: ID, preview?: string) => Promise<void>>;

  join(roomId: ID): Promise<void>;

  leave(roomId: ID): Promise<void>;

  getMarks(roomId: ID): Promise<{ [type: string]: number; }>;

  setMark(roomId: ID, timestamp: number): Promise<void>;

  setDelivery(roomId: ID, messageId: ID): Promise<void>;

  setAudioToggle(callId: ID, enabled: boolean): Promise<void>;

  setVideoToggle(callId: ID, enabled: boolean): Promise<void>;

  latestMessages(
    roomId: ID,
    limit: number,
    messageType?: HistoryFilter
  ): Promise<ReadonlyArray<roomEvents.RoomEvent>>;

  pagedMessages(
    roomId: ID,
    limit: number,
    timestamp?: number,
    messageType?: HistoryFilter
  ): Promise<ReadonlyArray<roomEvents.RoomEvent>>;

  onCallInvitation(callback: (c: callEvents.Invited) => void): void;

  onRemoteTrack(callId: ID, callback: (peerId: ID, track: MediaStreamTrack) => void): void;

  answerCall(callId: ID, tracks: Array<MediaStreamTrack>): void;

  rejectCall(callId: ID): Promise<void>;
  leaveCall(callId: ID): Promise<void>;

  onLeftCall(callId: ID, callback: (msg: callEvents.Left) => void): void;

  onEndCall(callId: ID, callback: (msg: callEvents.Ended) => void): void;

  onActiveDevice(callId: ID, callback: (msg: callEvents.CallHandledOnDevice) => void): void;

  onRoomLoad(roomId: ID, callback: (room: GroupRoom) => void): void;

  getCall(callId: ID): Promise<Call>;

  getCurrentCall(): Call | undefined;

  storeCall(callId: string): Promise<Call>;

  //// Global subscriptions
  onDisconnected(callback: () => void): void;
  onUnauthorized(callback: () => void): void;

  onMeetingScheduled(callback: (event: externalEvents.MeetingScheduled) => void): void;
  onMeetingRescheduled(callback: (event: externalEvents.MeetingRescheduled) => void): void;
  onMeetingCancelled(callback: (event: externalEvents.MeetingCancelled) => void): void;
  onFollowerAdded(callback: (event: externalEvents.FollowerAdded) => void): void;
  onFollowerRemoved(callback: (event: externalEvents.FollowerRemoved) => void): void;
  onAllFollowersRemoved(callback: (event: externalEvents.AllFollowersRemoved) => void): void;
  onAssigneeChanged(callback: (event: externalEvents.AssigneeChanged) => void): void;
  onAssigneeRemoved(callback: (event: externalEvents.AssigneeRemoved) => void): void;
  onUnreadCountUpdated(callback: (event: externalEvents.UnreadCountUpdated) => void): void;
  onUnreadTotalUpdated(callback: (event: externalEvents.UnreadTotalUpdated) => void): void;
  onUnassignedCountUpdated(callback: (event: externalEvents.UnassignedCountUpdated) => void): void;
  onAgentGroupCreated(callback: (event: externalEvents.AgentGroupCreated) => void): void;
  onAgentGroupUpdated(callback: (event: externalEvents.AgentGroupUpdated) => void): void;
  onAgentGroupDeleted(callback: (event: externalEvents.AgentGroupDeleted) => void): void;
  onAdviserTagGroupsUpdated(callback: (event: externalEvents.AdviserTagGroupsUpdated) => void): void;
  onConversationStatusChanged(
    callback: (event: externalEvents.ConversationStatusChanged) => void
  ): void;
  onGuestProfileUpdated(callback: (event: externalEvents.GuestProfileUpdated) => void): void;
  onLastAdviserTimestampRemoved(
    callback: (event: externalEvents.LastAdviserTimestampRemoved) => void
  ): void;
  onLastAdviserTimestampSet(
    callback: (event: externalEvents.LastAdviserTimestampSet) => void
  ): void;
  onNotificationUpcomingMeeting(
    callback: (event: externalEvents.NotificationUpcomingMeeting) => void
  ): void;
  onAgentStatusUpdated(callback: (event: externalEvents.AgentStatusUpdated) => void): void;
  onAssigneeStatusUpdated(callback: (event: externalEvents.AssigneeStatusUpdated) => void): void;
  onLeadPresenceUpdated(callback: (event: externalEvents.LeadPresenceUpdated) => void): void;
  onConversationSnoozed(callback: (event: externalEvents.ConversationSnoozed) => void): void;
  onConversationUnsnoozed(callback: (event: externalEvents.ConversationUnsnoozed) => void): void;
  onCallCreated(callback: (event: callEvents.Created) => void): void;
  onCustomMessage(callback: (message: roomEvents.CustomMessageSent) => void): void;

  //// Rooms subscriptions
  onRoomMessage(
    roomId: ID,
    callback: (message: roomEvents.MessageSent) => void,
    unsubscribe$?: Observable<void>
  ): Promise<void>;
  onRoomCustomMessage(
    roomId: ID,
    callback: (message: roomEvents.CustomMessageSent) => void,
    unsubscribe$?: Observable<void>
  ): Promise<void>;

  onRoomMessageUpdated(
    roomId: ID,
    callback: (message: roomEvents.MessageUpdated) => void,
    unsubscribe$?: Observable<void>
  ): Promise<void>;

  onRoomTyping(
    roomId: ID,
    callback: (typing: roomEvents.TypingSent) => void,
    unsubscribe$?: Observable<void>
  ): Promise<void>;

  onRoomMarked(
    roomId: ID,
    callback: (mark: roomEvents.MarkSent) => void,
    unsubscribe$?: Observable<void>
  ): Promise<void>;

  onCustom(
    roomId: ID,
    tag: MessageTag,
    callback: (message: roomEvents.CustomMessageSent) => void,
    unsubscribe$?: Observable<void>
  ): Promise<void>;
}

@Timed
export class DefaultArtichokeService implements ArtichokeService {
  private connectionSubscription?: Subscription;
  private readonly disconnect$ = new Subject<void>();
  private readonly connectedEvent = new Subject<void>();
  public disconnectedEvent: Observable<any> = EMPTY;

  private session?: Session;
  private rooms: Map<ID, Promise<GroupRoom>>;
  private calls: Map<ID, Promise<Call>>;
  private handlers: Map<ID, Array<RoomHandler>>;
  private deviceId?: Promise<string>;
  private call?: Call;

  constructor() {
    this.rooms = new Map();
    this.calls = new Map();
    this.handlers = new Map();
  }

  private get service(): Artichoke {
    if (!this.session) {
      throw new Error("There is no an active session established");
    }
    return this.session.artichoke;
  }

  connect(id: ID) {
    const closerSdk = CloserSDK.init();
    this.session = closerSdk.withSession(id);

    this.connectionSubscription = this.service.connection$.subscribe(() => {
      this.connectedEvent.next();
    });

    // setup basic error logger
    this.setErrorLogger();

    this.disconnectedEvent = merge(this.disconnect$, this.service.serverUnreachable$);

    this.disconnectedEvent.subscribe(() => {
      this.clearCachedData();
    });
  }

  private setErrorLogger() {
    this.service.error$.subscribe((error) =>
      logger.error({ message: "Artichoke service error", error })
    );
  }

  private clearCachedData() {
    this.rooms.clear();
    this.calls.clear();
    this.handlers.clear();
  }

  onConnected(callback: () => void) {
    // connectedEvent completes only when connection is closed normally (ev.code === 1000).
    // Otherwise it waits for reconnections process.
    this.connectedEvent
      .pipe(takeUntil((this.session && this.disconnect$) || EMPTY))
      .subscribe(callback);
  }

  disconnect() {
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.disconnect$.next();
    }
  }

  getDeviceId(): Promise<ID> | undefined {
    return this.deviceId;
  }

  send(roomId: ID, text: string, context?: any): Promise<chatEvents.Received> {
    return this.getRoom(roomId).then((room) => firstValueFrom(room.send(text, context)));
  }

  sendCustom(roomId: ID, text: string, tag: string, context: any): Promise<chatEvents.Received> {
    return this.getRoom(roomId).then((room) => firstValueFrom(room.sendCustom(text, tag, context)));
  }

  indicateTyping(roomId: ID, preview?: string): Promise<void> {
    return this.getRoom(roomId).then((room) => room.indicateTyping(preview));
  }

  indicateTypingThrottled = throttle(
    (roomId: ID, preview?: string) => this.indicateTyping(roomId, preview),
    config.services.times.chatTypingThrottleMillis
  );

  join(roomId: ID): Promise<void> {
    return this.getRoom(roomId).then((room) => room.join());
  }

  leave(roomId: ID): Promise<void> {
    return this.getRoom(roomId).then((room) => room.leave());
  }

  getMarks(roomId: ID): Promise<{ [type: string]: number; }> {
    return this.getRoom(roomId).then((room) => room.marks);
  }

  setMark(roomId: ID, timestamp: number): Promise<void> {
    return this.getRoom(roomId).then((room) => room.setMark(timestamp));
  }

  setDelivery(roomId: ID, messageId: ID): Promise<void> {
    return this.getRoom(roomId).then((room) => room.setDelivered(messageId));
  }

  setAudioToggle(callId: ID, enabled: boolean): Promise<void> {
    return this.getCall(callId).then((call) => call.setAudioToggle(enabled, Date.now()));
  }

  setVideoToggle(callId: ID, enabled: boolean): Promise<void> {
    return this.getCall(callId).then((call) => call.setVideoToggle(enabled, Date.now()));
  }

  async latestMessages(
    roomId: ID,
    limit: number,
    messageType?: HistoryFilter
  ): Promise<ReadonlyArray<roomEvents.RoomEvent>> {
    const room = await this.getRoom(roomId);
    const messages = await room.getLatestMessages(limit, messageType);
    // rest of the methods rely on ascending order
    return [...messages].sort((a, b) => a.timestamp - b.timestamp);
  }

  async pagedMessages(
    roomId: ID,
    limit: number,
    timestamp?: number,
    messageType?: HistoryFilter
  ): Promise<ReadonlyArray<roomEvents.RoomEvent>> {
    const room = await this.getRoom(roomId);
    const messages = await room.getMessages(limit, timestamp, messageType);
    // rest of the methods rely on ascending order
    return [...messages].sort((a, b) => a.timestamp - b.timestamp);
  }

  onCallInvitation(callback: (c: callEvents.Invited) => void) {
    this.service.callInvitation$.subscribe((ci) => {
      this.service.getCall(ci.callId).then((call) => {
        this.putCall(call);
        callback(ci);
      });
    });
  }

  onRemoteTrack(callId: ID, callback: (peerId: ID, track: MediaStreamTrack) => void) {
    this.getCall(callId).then((call) =>
      call.remoteTrack$.subscribe((track) => callback(track.peerId, track.track))
    );
  }

  answerCall(callId: ID, tracks: Array<MediaStreamTrack>) {
    this.getCall(callId).then((call) => call.answer(tracks));
  }

  rejectCall(callId: ID) {
    return this.getCall(callId).then((call) => call.reject(CallReason.CallRejected));
  }

  leaveCall(callId: ID) {
    return this.getCall(callId).then((call) => call.leave(CallReason.Hangup));
  }

  onLeftCall(callId: ID, callback: (msg: callEvents.Left) => void) {
    this.getCall(callId).then((call) => call.left$.subscribe((msg) => callback(msg)));
  }

  onEndCall(callId: ID, callback: (msg: callEvents.Ended) => void) {
    this.getCall(callId).then((call) => call.end$.subscribe((msg) => callback(msg)));
  }

  onActiveDevice(callId: ID, callback: (msg: callEvents.CallHandledOnDevice) => void) {
    this.getCall(callId).then((call) => call.activeDevice$.subscribe((msg) => callback(msg)));
  }

  onRoomLoad(roomId: ID, callback: (room: GroupRoom) => void) {
    const cachedRoom = this.rooms.get(roomId);
    if (cachedRoom) {
      cachedRoom.then((room) => callback(room));
    } else {
      const handlers = this.handlers.get(roomId) || [];
      this.handlers.set(roomId, handlers.concat([callback]));
    }
  }

  getCall(callId: ID): Promise<Call> {
    const cachedCall = this.calls.get(callId);
    if (cachedCall) {
      return cachedCall;
    } else {
      const callPromise = this.service.getCall(callId).then((c) => Promise.resolve(c));
      this.calls.set(callId, callPromise);
      return callPromise;
    }
  }

  getCurrentCall(): Call | undefined {
    return this.call;
  }

  storeCall(callId: string): Promise<Call> {
    if (!this.session) {
      return Promise.reject("Session was never initialized!");
    }
    return this.getCall(callId).then((call) => {
      this.call = call;
      return call;
    });
  }

  //// Global subscriptions
  onDisconnected(callback: () => void) {
    this.service.disconnect$.pipe(takeUntil(this.disconnect$)).subscribe(() => {
      if (!this.connectionSubscription?.closed) {
        callback();
      }
    });
  }
  onUnauthorized(callback: () => void) {
    this.service.unauthorized$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }

  onMeetingScheduled(callback: (event: externalEvents.MeetingScheduled) => void) {
    this.service.meetingScheduled$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onMeetingRescheduled(callback: (event: externalEvents.MeetingRescheduled) => void) {
    this.service.meetingRescheduled$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onMeetingCancelled(callback: (event: externalEvents.MeetingCancelled) => void) {
    this.service.meetingCancelled$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onFollowerAdded(callback: (event: externalEvents.FollowerAdded) => void) {
    this.service.followerAdded$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onFollowerRemoved(callback: (event: externalEvents.FollowerRemoved) => void) {
    this.service.followerRemoved$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAllFollowersRemoved(callback: (event: externalEvents.AllFollowersRemoved) => void) {
    this.service.allFollowersRemoved$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAssigneeChanged(callback: (event: externalEvents.AssigneeChanged) => void) {
    this.service.assigneeChanged$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAssigneeRemoved(callback: (event: externalEvents.AssigneeRemoved) => void) {
    this.service.assigneeRemoved$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onUnreadCountUpdated(callback: (event: externalEvents.UnreadCountUpdated) => void) {
    this.service.unreadCountUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onUnreadTotalUpdated(callback: (event: externalEvents.UnreadTotalUpdated) => void) {
    this.service.unreadTotalUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onUnassignedCountUpdated(callback: (event: externalEvents.UnassignedCountUpdated) => void) {
    this.service.unassignedCountUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAgentGroupCreated(callback: (event: externalEvents.AgentGroupCreated) => void) {
    this.service.agentGroupCreated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAgentGroupUpdated(callback: (event: externalEvents.AgentGroupUpdated) => void) {
    this.service.agentGroupUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAgentGroupDeleted(callback: (event: externalEvents.AgentGroupDeleted) => void) {
    this.service.agentGroupDeleted$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAdviserTagGroupsUpdated(callback: (event: externalEvents.AdviserTagGroupsUpdated) => void) {
    this.service.adviserTagGroupsUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onConversationStatusChanged(callback: (event: externalEvents.ConversationStatusChanged) => void) {
    this.service.conversationStatusChanged$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onGuestProfileUpdated(callback: (event: externalEvents.GuestProfileUpdated) => void) {
    this.service.guestProfileUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onLastAdviserTimestampRemoved(
    callback: (event: externalEvents.LastAdviserTimestampRemoved) => void
  ) {
    this.service.lastAdviserTimestampRemoved$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onLastAdviserTimestampSet(callback: (event: externalEvents.LastAdviserTimestampSet) => void) {
    this.service.lastAdviserTimestampSet$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onNotificationUpcomingMeeting(
    callback: (event: externalEvents.NotificationUpcomingMeeting) => void
  ) {
    this.service.notificationUpcomingMeeting$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAgentStatusUpdated(callback: (event: externalEvents.AgentStatusUpdated) => void): void {
    this.service.agentStatusUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onAssigneeStatusUpdated(callback: (event: externalEvents.AssigneeStatusUpdated) => void) {
    this.service.assigneeStatusUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onLeadPresenceUpdated(callback: (event: externalEvents.LeadPresenceUpdated) => void): void {
    this.service.leadPresenceUpdated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onConversationSnoozed(callback: (event: externalEvents.ConversationSnoozed) => void) {
    this.service.conversationSnoozed$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onConversationUnsnoozed(callback: (event: externalEvents.ConversationUnsnoozed) => void) {
    this.service.conversationUnsnoozed$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onCallCreated(callback: (event: callEvents.Created) => void) {
    this.service.callCreated$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }
  onCustomMessage(callback: (message: roomEvents.CustomMessageSent) => void) {
    this.service.customMessage$.pipe(takeUntil(this.disconnect$)).subscribe(callback);
  }

  //// Rooms subscriptions
  async onRoomMessage(
    roomId: ID,
    callback: (message: roomEvents.MessageSent) => void,
    unsubscribe$?: Observable<void>
  ) {
    const room = await this.getRoom(roomId);

    const unsubscribe = unsubscribe$
      ? merge(unsubscribe$, this.disconnectedEvent)
      : this.disconnectedEvent;
    room.message$.pipe(takeUntil(unsubscribe)).subscribe(callback);
  }

  async onRoomCustomMessage(
    roomId: ID,
    callback: (message: roomEvents.CustomMessageSent) => void,
    unsubscribe$?: Observable<void>
  ) {
    const room = await this.getRoom(roomId);

    const unsubscribe = unsubscribe$
      ? merge(unsubscribe$, this.disconnectedEvent)
      : this.disconnectedEvent;
    room.customMessage$.pipe(takeUntil(unsubscribe)).subscribe(callback);
  }

  async onRoomMessageUpdated(
    roomId: ID,
    callback: (message: roomEvents.MessageUpdated) => void,
    unsubscribe$?: Observable<void>
  ) {
    const room = await this.getRoom(roomId);

    const unsubscribe = unsubscribe$
      ? merge(unsubscribe$, this.disconnectedEvent)
      : this.disconnectedEvent;
    room.messageUpdated$.pipe(takeUntil(unsubscribe)).subscribe(callback);
  }

  async onRoomTyping(
    roomId: ID,
    callback: (typing: roomEvents.TypingSent) => void,
    unsubscribe$?: Observable<void>
  ) {
    const room = await this.getRoom(roomId);

    const unsubscribe = unsubscribe$
      ? merge(unsubscribe$, this.disconnectedEvent)
      : this.disconnectedEvent;
    room.typing$.pipe(takeUntil(unsubscribe)).subscribe(callback);
  }

  async onRoomMarked(
    roomId: ID,
    callback: (mark: roomEvents.MarkSent) => void,
    unsubscribe$?: Observable<void>
  ) {
    const room = await this.getRoom(roomId);

    const unsubscribe = unsubscribe$
      ? merge(unsubscribe$, this.disconnectedEvent)
      : this.disconnectedEvent;
    room.marked$.pipe(takeUntil(unsubscribe)).subscribe(callback);
  }

  async onCustom(
    roomId: ID,
    tag: MessageTag,
    callback: (message: roomEvents.CustomMessageSent) => void,
    unsubscribe$?: Observable<void>
  ) {
    const room = await this.getRoom(roomId);

    const unsubscribe = unsubscribe$
      ? merge(unsubscribe$, this.disconnectedEvent)
      : this.disconnectedEvent;
    room.customSubtagMessage$(tag).pipe(takeUntil(unsubscribe)).subscribe(callback);
  }

  private getRoom(roomId: ID): Promise<GroupRoom> {
    const cachedRoom = this.rooms.get(roomId);
    if (cachedRoom) {
      return cachedRoom;
    } else {
      const roomPromise = this.service.getRoom(roomId).then((r) => {
        const groupRoom = r as GroupRoom;
        const handlers = this.handlers.get(roomId);
        if (handlers) {
          handlers.forEach((h) => h(groupRoom));
        }
        return Promise.resolve(groupRoom);
      });
      this.rooms.set(roomId, roomPromise);
      return roomPromise;
    }
  }

  private putCall(call: Call): void {
    this.calls.set(call.id, Promise.resolve(call));
  }
}
